'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { ThemeContext, ThemeProvider as ThemeProvider$1, Global } from "../../@emotion/react";
export { keyframes } from "../../@emotion/react";
import React, { useContext, useRef, forwardRef as forwardRef$1, useMemo } from 'react';
import { useColorMode } from "../color-mode";
export * from "../color-mode";
import { resolveStyleConfig, propNames, isStyleProp, css, toCSSVar } from "../styled-system";
export * from "../styled-system";
import { mergeThemeOverride } from "../theme-utils";
import { memoizedGet, mergeWith, filterUndefined, omit, objectFilter, runIfFn } from "../utils";
import { assignAfter } from "../object-utils";
import createStyled from "../../@emotion/styled";
import { createContext } from "../react-utils";
import { jsxs, jsx } from 'react/jsx-runtime'; // src/use-theme.ts

function useTheme() {
  const theme = useContext(ThemeContext);

  if (!theme) {
    throw Error("useTheme: `theme` is undefined. Seems you forgot to wrap your app in `<ChakraProvider />` or `<ThemeProvider />`");
  }

  return theme;
}

function useChakra() {
  const colorModeResult = useColorMode();
  const theme = useTheme();
  return Object.assign({}, colorModeResult, {
    theme
  });
}

function getBreakpointValue(theme, value, fallback) {
  var _a, _b;

  if (value == null) return value;

  const getValue = val => {
    var _a2, _b2;

    return (_b2 = (_a2 = theme.__breakpoints) == null ? void 0 : _a2.asArray) == null ? void 0 : _b2[val];
  };

  return (_b = (_a = getValue(value)) != null ? _a : getValue(fallback)) != null ? _b : fallback;
}

function getTokenValue(theme, value, fallback) {
  var _a, _b;

  if (value == null) return value;

  const getValue = val => {
    var _a2, _b2;

    return (_b2 = (_a2 = theme.__cssMap) == null ? void 0 : _a2[val]) == null ? void 0 : _b2.value;
  };

  return (_b = (_a = getValue(value)) != null ? _a : getValue(fallback)) != null ? _b : fallback;
}

function useToken(scale, token, fallback) {
  const theme = useTheme();
  return getToken(scale, token, fallback)(theme);
}

function getToken(scale, token, fallback) {
  const _token = Array.isArray(token) ? token : [token];

  const _fallback = Array.isArray(fallback) ? fallback : [fallback];

  return theme => {
    const fallbackArr = _fallback.filter(Boolean);

    const result = _token.map((token2, index) => {
      var _a, _b;

      if (scale === "breakpoints") {
        return getBreakpointValue(theme, token2, (_a = fallbackArr[index]) != null ? _a : token2);
      }

      const path = `${scale}.${token2}`;
      return getTokenValue(theme, path, (_b = fallbackArr[index]) != null ? _b : token2);
    });

    return Array.isArray(token) ? result : result[0];
  };
}
/* global Map:readonly, Set:readonly, ArrayBuffer:readonly */


var hasElementType = typeof Element !== 'undefined';
var hasMap = typeof Map === 'function';
var hasSet = typeof Set === 'function';
var hasArrayBuffer = typeof ArrayBuffer === 'function' && !!ArrayBuffer.isView; // Note: We **don't** need `envHasBigInt64Array` in fde es6/index.js

function equal(a, b) {
  // START: fast-deep-equal es6/index.js 3.1.3
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;
    var length, i, keys;

    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;

      for (i = length; i-- !== 0;) if (!equal(a[i], b[i])) return false;

      return true;
    } // START: Modifications:
    // 1. Extra `has<Type> &&` helpers in initial condition allow es6 code
    //    to co-exist with es5.
    // 2. Replace `for of` with es5 compliant iteration using `for`.
    //    Basically, take:
    //
    //    ```js
    //    for (i of a.entries())
    //      if (!b.has(i[0])) return false;
    //    ```
    //
    //    ... and convert to:
    //
    //    ```js
    //    it = a.entries();
    //    while (!(i = it.next()).done)
    //      if (!b.has(i.value[0])) return false;
    //    ```
    //
    //    **Note**: `i` access switches to `i.value`.


    var it;

    if (hasMap && a instanceof Map && b instanceof Map) {
      if (a.size !== b.size) return false;
      it = a.entries();

      while (!(i = it.next()).done) if (!b.has(i.value[0])) return false;

      it = a.entries();

      while (!(i = it.next()).done) if (!equal(i.value[1], b.get(i.value[0]))) return false;

      return true;
    }

    if (hasSet && a instanceof Set && b instanceof Set) {
      if (a.size !== b.size) return false;
      it = a.entries();

      while (!(i = it.next()).done) if (!b.has(i.value[0])) return false;

      return true;
    } // END: Modifications


    if (hasArrayBuffer && ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
      length = a.length;
      if (length != b.length) return false;

      for (i = length; i-- !== 0;) if (a[i] !== b[i]) return false;

      return true;
    }

    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags; // START: Modifications:
    // Apply guards for `Object.create(null)` handling. See:
    // - https://github.com/FormidableLabs/react-fast-compare/issues/64
    // - https://github.com/epoberezkin/fast-deep-equal/issues/49

    if (a.valueOf !== Object.prototype.valueOf && typeof a.valueOf === 'function' && typeof b.valueOf === 'function') return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString && typeof a.toString === 'function' && typeof b.toString === 'function') return a.toString() === b.toString(); // END: Modifications

    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0;) if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false; // END: fast-deep-equal
    // START: react-fast-compare
    // custom handling for DOM elements


    if (hasElementType && a instanceof Element) return false; // custom handling for React/Preact

    for (i = length; i-- !== 0;) {
      if ((keys[i] === '_owner' || keys[i] === '__v' || keys[i] === '__o') && a.$$typeof) {
        // React-specific: avoid traversing React elements' _owner
        // Preact-specific: avoid traversing Preact elements' __v and __o
        //    __v = $_original / $_vnode
        //    __o = $_owner
        // These properties contain circular references and are not needed when
        // comparing the actual elements (and not their owners)
        // .$$typeof and ._store on just reasonable markers of elements
        continue;
      } // all other properties should be traversed as usual


      if (!equal(a[keys[i]], b[keys[i]])) return false;
    } // END: react-fast-compare
    // START: fast-deep-equal


    return true;
  }

  return a !== a && b !== b;
} // end fast-deep-equal


var reactFastCompare = function isEqual(a, b) {
  try {
    return equal(a, b);
  } catch (error) {
    if ((error.message || '').match(/stack|recursion/i)) {
      // warn on circular references, don't crash
      // browsers give this different errors name and messages:
      // chrome/safari: "RangeError", "Maximum call stack size exceeded"
      // firefox: "InternalError", too much recursion"
      // edge: "Error", "Out of stack space"
      console.warn('react-fast-compare cannot handle circular refs');
      return false;
    } // some other error. we should definitely know about these


    throw error;
  }
};

function useStyleConfigImpl(themeKey, props = {}) {
  var _a;

  const {
    styleConfig: styleConfigProp
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["styleConfig"]);

  const {
    theme,
    colorMode
  } = useChakra();
  const themeStyleConfig = themeKey ? memoizedGet(theme, `components.${themeKey}`) : void 0;
  const styleConfig = styleConfigProp || themeStyleConfig;
  const mergedProps = mergeWith({
    theme,
    colorMode
  }, (_a = styleConfig == null ? void 0 : styleConfig.defaultProps) != null ? _a : {}, filterUndefined(omit(rest, ["children"])));
  const stylesRef = useRef({});

  if (styleConfig) {
    const getStyles = resolveStyleConfig(styleConfig);
    const styles = getStyles(mergedProps);
    const isStyleEqual = reactFastCompare(stylesRef.current, styles);

    if (!isStyleEqual) {
      stylesRef.current = styles;
    }
  }

  return stylesRef.current;
}

function useStyleConfig(themeKey, props = {}) {
  return useStyleConfigImpl(themeKey, props);
}

function useMultiStyleConfig(themeKey, props = {}) {
  return useStyleConfigImpl(themeKey, props);
}

function useComponentStyles__unstable(themeKey, props) {
  var _a;

  const {
    baseConfig
  } = props,
        restProps = _objectWithoutPropertiesLoose(props, ["baseConfig"]);

  const {
    theme
  } = useChakra();
  const overrides = (_a = theme.components) == null ? void 0 : _a[themeKey];
  const styleConfig = overrides ? mergeThemeOverride(overrides, baseConfig) : baseConfig;
  return useStyleConfigImpl(null, Object.assign({}, restProps, {
    styleConfig
  }));
} // src/should-forward-prop.ts


var allPropNames = /* @__PURE__ */new Set([...propNames, "textStyle", "layerStyle", "apply", "noOfLines", "focusBorderColor", "errorBorderColor", "as", "__css", "css", "sx"]);
var validHTMLProps = /* @__PURE__ */new Set(["htmlWidth", "htmlHeight", "htmlSize", "htmlTranslate"]);

function shouldForwardProp(prop) {
  return validHTMLProps.has(prop) || !allPropNames.has(prop);
}

var _a;

var emotion_styled = (_a = createStyled.default) != null ? _a : createStyled;

var toCSSObject = ({
  baseStyle
}) => props => {
  const {
    css: cssProp,
    __css,
    sx
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["theme", "css", "__css", "sx"]);

  const styleProps = objectFilter(rest, (_, prop) => isStyleProp(prop));
  const finalBaseStyle = runIfFn(baseStyle, props);
  const finalStyles = assignAfter({}, __css, finalBaseStyle, filterUndefined(styleProps), sx);
  const computedCSS = css(finalStyles)(props.theme);
  return cssProp ? [computedCSS, cssProp] : computedCSS;
};

function styled(component, options) {
  const _ref = options != null ? options : {},
        {
    baseStyle
  } = _ref,
        styledOptions = _objectWithoutPropertiesLoose(_ref, ["baseStyle"]);

  if (!styledOptions.shouldForwardProp) {
    styledOptions.shouldForwardProp = shouldForwardProp;
  }

  const styleObject = toCSSObject({
    baseStyle
  });
  const Component = emotion_styled(component, styledOptions)(styleObject);
  const chakraComponent = /*#__PURE__*/React.forwardRef(function ChakraComponent(props, ref) {
    const {
      colorMode,
      forced
    } = useColorMode();
    return /*#__PURE__*/React.createElement(Component, Object.assign({
      ref,
      "data-theme": forced ? colorMode : void 0
    }, props));
  });
  return chakraComponent;
} // src/factory.ts


function factory() {
  const cache = /* @__PURE__ */new Map();
  return new Proxy(styled, {
    apply(target, thisArg, argArray) {
      return styled(...argArray);
    },

    get(_, element) {
      if (!cache.has(element)) {
        cache.set(element, styled(element));
      }

      return cache.get(element);
    }

  });
}

var chakra = factory(); // src/forward-ref.tsx

function forwardRef(component) {
  return /*#__PURE__*/forwardRef$1(component);
} // src/providers.tsx


function ThemeProvider(props) {
  const {
    cssVarsRoot,
    theme,
    children
  } = props;
  const computedTheme = useMemo(() => toCSSVar(theme), [theme]);
  return /* @__PURE__ */jsxs(ThemeProvider$1, {
    theme: computedTheme,
    children: [/* @__PURE__ */jsx(CSSVars, {
      root: cssVarsRoot
    }), children]
  });
}

function CSSVars({
  root = ":host, :root"
}) {
  const selector = [root, `[data-theme]`].join(",");
  return /* @__PURE__ */jsx(Global, {
    styles: theme => ({
      [selector]: theme.__cssVars
    })
  });
}

var [StylesProvider, useStyles] = createContext({
  name: "StylesContext",
  errorMessage: "useStyles: `styles` is undefined. Seems you forgot to wrap the components in `<StylesProvider />` "
});

function createStylesContext(componentName) {
  return createContext({
    name: `${componentName}StylesContext`,
    errorMessage: `useStyles: "styles" is undefined. Seems you forgot to wrap the components in "<${componentName} />" `
  });
}

function GlobalStyle() {
  const {
    colorMode
  } = useColorMode();
  return /* @__PURE__ */jsx(Global, {
    styles: theme => {
      const styleObjectOrFn = memoizedGet(theme, "styles.global");
      const globalStyles = runIfFn(styleObjectOrFn, {
        theme,
        colorMode
      });
      if (!globalStyles) return void 0;
      const styles = css(globalStyles)(theme);
      return styles;
    }
  });
}

export { CSSVars, GlobalStyle, StylesProvider, ThemeProvider, chakra, createStylesContext, forwardRef, getToken, shouldForwardProp, styled, toCSSObject, useChakra, useComponentStyles__unstable, useMultiStyleConfig, useStyleConfig, useStyles, useTheme, useToken };